import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import styled from 'styled-components/macro';
import { connect } from 'react-redux';
import AZButton from './AZButton';
import SectionTitle from './SectionTitle';
import LoadingError from './alerts/LoadingError';
import { fetchAZLetters, azFetchData } from '../actions';
import Isvg from 'react-inlinesvg';
import CardGridContainer from './CardGridContainer';
import CardGrid from './CardGrid';
import CSSTransitionGroup from 'react-addons-css-transition-group';
import translate from './TranslateHOC';
import IsLoadingAnim from './alerts/IsLoadingAnim';
import SimpleBarReact from 'simplebar-react';
import cancel from '../img/icons/circle_close.svg';

function AZQuickSearch({
  strings,
  azLetters,
  azLettersIsLoading,
  azLettersHasErrored,
  azIsLoading,
  azHasErrored,
  azProgrammes,
  fetchAZLetters,
  azData,
  curLang,
}) {
  const [showAZ, setShowAZ] = useState(false);
  const [activeLetter, setActiveLetter] = useState({});
  const [search, setSearch] = useState('');
  const memoizedItems = useMemo(() => azProgrammes, [azProgrammes]);

  useEffect(() => {
    setSearch('');
    fetchAZLetters();
    azData('');
    setActiveLetter({});
    setShowAZ(false);
    window.scrollTo(0, 0);
  }, [fetchAZLetters, curLang, azData]);

  useEffect(() => {
    if (!_.isEqual(memoizedItems, azProgrammes)) {
      memoizedItems.current = azProgrammes;
    }
  }, [azProgrammes, memoizedItems]);

  useEffect(() => {
    azData(search);
  }, [search, azData]);

  const getAZProgrammes = (event, search) => {
    event.preventDefault();
    setSearch(search);
    setShowAZ(true);
    setActiveLetter({ [search]: true });
  };

  const closeAZProgrammes = (event) => {
    event.preventDefault();
    setShowAZ(false);
    setActiveLetter({});
  };

  if (azLettersHasErrored) {
    return (
      <CardGridContainer type="az-quick">
        <LoadingError errorMessage={strings.sorryLoading} />
      </CardGridContainer>
    );
  }

  if (azLettersIsLoading) {
    return <IsLoadingAnim />;
  }

  const AZButtonWrapper = ({ condition, wrapper, children }) =>
    condition ? wrapper(children) : children;

  return (
    <CardGridContainer type="az-quick">
      <SectionTitleContainer>
        <SectionTitle title={`${strings.azSearch}`} type="no-spacing" />
      </SectionTitleContainer>
      <SimpleBarReact style={{ maxWidth: '100%' }}>
        {Object.values(azLetters).length > 0 && (
          <UL>
            {Object.values(azLetters).map(({ search, display }) => (
              <LI key={search}>
                <AZButtonWrapper
                  condition={!activeLetter[search]}
                  wrapper={(children) => (
                    <AZButton
                      to="az"
                      spy
                      smooth={false}
                      offset={-100}
                      duration={300}
                      delay={0}
                      onClick={(event) => getAZProgrammes(event, search)}
                    >
                      {children}
                    </AZButton>
                  )}
                >
                  {activeLetter[search] ? (
                    <ActiveButton onClick={closeAZProgrammes}>
                      {display}
                      {showAZ && activeLetter[search] && (
                        <CloseButton>
                          <Isvg src={cancel} />
                        </CloseButton>
                      )}
                    </ActiveButton>
                  ) : (
                    <>{display}</>
                  )}
                </AZButtonWrapper>
              </LI>
            ))}
          </UL>
        )}
      </SimpleBarReact>
      <AZQuickProgrammes>
        <CSSTransitionGroup
          component="div"
          transitionName="az-animate"
          transitionEnterTimeout={300}
          transitionLeaveTimeout={150}
        >
          {showAZ && (
            <CardGrid
              orientation="horizontal"
              items={memoizedItems}
              itemsIsLoading={azIsLoading}
              itemsHasErrored={azHasErrored}
              gridType="az"
            />
          )}
        </CSSTransitionGroup>
      </AZQuickProgrammes>
    </CardGridContainer>
  );
}

const SectionTitleContainer = styled.div`
  margin: 0 !important;
  padding: 20px 10px 22px;
  @media screen and (min-width: 768px) {
    margin-bottom: 12px;
    padding: 24px 0 22px;
  }
`;

const UL = styled.ul`
  margin: 0 10px;
  padding: 0 2px 24px;
  white-space: nowrap;
  padding-left: 2px;
  padding-top: 2px;
  @media screen and (min-width: 768px) {
    margin: 0;
  }
`;

const LI = styled.li`
  position: relative;
  display: inline;
  list-style: none;
  margin: 0 8px 0 0;
  padding: 0;
`;

const ActiveButton = styled.button`
  ${(props) => props.theme.fontBold};
  background-color: ${(props) => props.theme.color.primaryFull};
  color: ${(props) => props.theme.color.blackFull};
  border: 0;
  border-radius: 20px;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  backdrop-filter: blur(24px);
  position: relative;
  display: inline-flex;
  padding: 4px 4px 4px 16px;
  align-items: center;
  &:focus-visible {
    box-shadow: 0px 0px 0px 2px
      ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.statusLightFocus
          : props.theme.color.statusDarkFocus};
    border-radius: 20px;
    outline: none;
  }
`;

const CloseButton = styled.div`
  height: 18px;
  margin: 0 4px;
  padding: 0;
  width: 18px;
  svg {
    height: 100%;
    width: 100%;
    g * {
      fill: ${(props) => props.theme.color.black90};
    }
  }
`;

const AZQuickProgrammes = styled.div`
  margin: 0 10px;
  padding: 24px 0 16px;
  @media screen and (min-width: 580px) {
    padding: 36px 0 24px !important;
  }
  @media screen and (min-width: 768px) {
    margin: 0;
    padding: 0 0 24px;
  }
`;

const mapStateToProps = (state) => {
  return {
    curLang: state.curLang,
    azLetters: state.azLetters.success,
    azLettersIsLoading: state.azLetters.loading,
    azLettersHasErrored: state.azLetters.error,
    azProgrammes: state.azProgrammes.success,
    azHasErrored: state.azProgrammes.error,
    azIsLoading: state.azProgrammes.loading,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchAZLetters: () => dispatch(fetchAZLetters()),
    azData: (search) => dispatch(azFetchData(search)),
  };
};

export default translate(
  connect(mapStateToProps, mapDispatchToProps)(AZQuickSearch)
);
